import { DynamicRecordFormModal } from "features/shared/components/dynamic-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { TechnicalDataScenarioFormState, TechnicalDataScenarioModalMode } from "../model/types";

import { buildScenarioFormFields } from "../lib/scenarioFormFields";

type ScenarioFormModalProps = {
  initialFormState: TechnicalDataScenarioFormState;
  mode: TechnicalDataScenarioModalMode;
  onClose: () => void;
  onSubmit: (formState: TechnicalDataScenarioFormState) => Promise<void> | void;
  open: boolean;
};

export const ScenarioFormModal = ({ initialFormState, mode, onClose, onSubmit, open }: ScenarioFormModalProps) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState(initialFormState);

  useEffect(() => {
    if (!open) return;
    setFormState(initialFormState);
  }, [initialFormState, open]);

  const onFieldChange = useCallback((field: keyof TechnicalDataScenarioFormState, value: unknown) => {
    setFormState((current) => ({ ...current, [field]: value }));
  }, []);

  const fields = useMemo(() => buildScenarioFormFields({ formState, onFieldChange, t }), [formState, onFieldChange, t]);

  const handleSave = async () => {
    if (!formState.name.trim()) {
      toast.error(t("TechnicalData.scenario.nameRequired"));
      return;
    }
    try {
      await onSubmit(formState);
      onClose();
    } catch {
      // API errors are toasted by the scenario mutations.
    }
  };

  return (
    <DynamicRecordFormModal
      columns={2}
      fields={fields}
      mode={mode === "add" ? "add" : "edit"}
      onClose={onClose}
      onSave={handleSave}
      open={open}
      title={t(mode === "add" ? "TechnicalData.scenario.addTitle" : "TechnicalData.scenario.editTitle")}
      width="48rem"
    />
  );
};
